import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Container,
    FormControl,
    Grid,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    Stack,
    Typography
} from "@mui/material";

import axios from "axios";
import {useNavigate} from "react-router-dom";
import {ArrowUpward, ArrowDownward} from "@mui/icons-material";
import {api_Routes} from "../../api_Routes";
import {Helper} from "../../tools/Helper";
import ViewOnScroll from "../../helpers/ViewOnScroll";

import { useEffect, useState } from "react";

const AdsListing = () => {
    const navigate = useNavigate();
    const [ads, setAds] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isAd, setIsAd] = useState(1);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 10,
        totalItems: 0,
    });
    const [sorting, setSorting] = useState({
        field: "created_at",
        order: "desc",
    });

    const fetchAds = async () => {
        try {
            setLoading(true);
            let url =
                api_Routes.UserAds.View +
                `?page=${pagination.currentPage}&per_page=${pagination.itemsPerPage}`;
            if (selectedCategory) {
                url += `&ad_category_id=${selectedCategory}`;
            }
            if (sorting.field) {
                url += `&sort_by=${sorting.field}`;
            }
            if (sorting.order) {
                url += `&sort_order=${sorting.order}`;
            }
            url += `&is_ad=${isAd}`;

            const {response, message} = await Helper.Get({
                url,
                hasToken: true,
            });
            console.log(response);
            setAds(response?.data);
            setPagination({
                ...pagination,
                totalPages: response?.pagination.last_page,
                totalItems: response?.pagination.total,
            });
        } catch (error) {
            console.error("Error fetching ads:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const {response, message} = await Helper.Get({
                    url: api_Routes.UserAds.getCategories,
                    hasToken: true,
                });
                if (response?.data) {
                    setCategories(response.data);
                }
                fetchAds();
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchInitialData();
    }, []);

    useEffect(() => {
        fetchAds();
    }, [pagination.currentPage, sorting, selectedCategory, isAd]);

    const handlePageChange = (event, page) => {
        setPagination((prev) => ({...prev, currentPage: page}));
    };

    const handleSortChange = (field, source) => {
        if (source === "sort_by")
            setSorting((prev) => ({
                field,
                order: prev.order,
            }));
        else {
            setSorting((prev) => ({
                field: prev.field,
                order: field,
            }));
        }
    };

    const handleAdClick = (adId) => {
        navigate(`/ads/${adId}`);
    };
    return (
        <Container maxWidth="xl" sx={{mt: 4, mb: 4}}>
            <Grid container spacing={3}>
                {/* Sidebar */}
                <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Categories
                            </Typography>
                            <List>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        selected={!selectedCategory}
                                        onClick={() => setSelectedCategory(null)}
                                    >
                                        <ListItemText primary="All Categories"/>
                                    </ListItemButton>
                                </ListItem>
                                {categories.map((category) => (
                                    <ListItem key={category.id} disablePadding>
                                        <ListItemButton
                                            selected={selectedCategory === category.id}
                                            onClick={() => {
                                                setSelectedCategory(category.id);
                                                setPagination((prev) => ({...prev, currentPage: 1}));
                                            }}
                                        >
                                            <ListItemText
                                                primary={category.name}
                                                secondary={category.description}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Main Content */}
                <Grid item xs={12} md={9}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{mb: 3}}
                    >
                        <Typography variant="h5" component="h1">
                            {pagination.totalItems} results found
                        </Typography>
                        <Box>
                            <FormControl
                                variant="outlined"
                                sx={{minWidth: 200, pr: 1, pb: {xs: 1}}}
                            >
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={sorting.order}
                                    onChange={(e) =>
                                        handleSortChange(e.target.value, "sort_order")
                                    }
                                    label="Sort Order"
                                >
                                    <MenuItem value="asc">
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            ascending
                                            <ArrowUpward fontSize="small"/>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="desc">
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            descending
                                            <ArrowDownward fontSize="small"/>
                                        </Stack>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl
                                variant="outlined"
                                sx={{minWidth: 200, pr: 1, pb: {xs: 1}}}
                            >
                                <InputLabel>Looking for</InputLabel>
                                <Select
                                    value={isAd}
                                    onChange={(e) => setIsAd(e.target.value)}
                                    label="Looking for"
                                >
                                    <MenuItem value={1}>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            Service
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value={0}>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            Selling a service
                                        </Stack>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl variant="outlined" sx={{minWidth: 200}}>
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={sorting.field}
                                    onChange={(e) => handleSortChange(e.target.value, "sort_by")}
                                    label="Sort By"
                                >
                                    <MenuItem value="created_at">
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            Date
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="price">
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            Price
                                        </Stack>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                    <ViewOnScroll
                        currentPage={pagination.currentPage}
                        perPage={pagination.itemsPerPage}
                        selectedCategory={selectedCategory}
                        sortingField={sorting.field}
                        sortingOrder={sorting.order}
                        isAd={isAd}

                    >
                        <Grid container spacing={3} sx={{mb: 4}}>
                            {loading ? (
                                Array.from({length: pagination.itemsPerPage}).map(
                                    (_, index) => (
                                        <Grid item xs={12} sm={6} lg={4} key={index}>
                                            <Skeleton variant="rectangular" height={200}/>
                                            <Skeleton/>
                                            <Skeleton width="60%"/>
                                        </Grid>
                                    )
                                )
                            ) : ads.length > 0 ? (
                                ads.map((ad) => (
                                    <Grid item xs={12} sm={6} lg={4} key={ad.id}>
                                        <Card
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                cursor: "pointer",
                                                "&:hover": {boxShadow: 3},
                                            }}
                                            onClick={() => handleAdClick(ad.id)}
                                        >
                                            {ad.ad_photos?.[0] && (
                                                <CardMedia
                                                    component="img"
                                                    height="200"
                                                    image={ad.ad_photos[0].url}
                                                    alt={ad.title}
                                                />
                                            )}

                                            <CardContent sx={{flexGrow: 1}}>
                                                <Typography gutterBottom variant="h6" component="div">
                                                    {ad.title}
                                                </Typography>


                                                {ad.ad_sub_category_type && (
                                                    <Chip
                                                        label={ad.ad_sub_category_type.name}
                                                        size="small"
                                                        sx={{mb: 1}}
                                                    />
                                                )}

                                                {ad.is_show_price ? (
                                                    <Typography
                                                        variant="body1"
                                                        color="primary"
                                                        sx={{mb: 1}}
                                                    >
                                                        ${ad.price}
                                                    </Typography>
                                                ) : (
                                                    <></>
                                                )}

                                                {ad.description && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{mb: 2}}
                                                    >
                                                        {ad.description.length > 100
                                                            ? `${ad.description.substring(0, 100)}...`
                                                            : ad.description}
                                                    </Typography>
                                                )}

                                                <div>
                                                    {ad.phone && (
                                                        <Typography variant="body2">📞 {ad.phone}</Typography>
                                                    )}
                                                    {ad.email && (
                                                        <Typography variant="body2">✉️ {ad.email}</Typography>
                                                    )}
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{mb: 2}}>
                                                        Posted in : {ad.created_at}
                                                    </Typography>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Typography variant="h6" textAlign="center" sx={{py: 4}}>
                                        No ads found matching your criteria
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </ViewOnScroll>


                    {!loading && pagination.totalPages > 1 && (
                        <Stack direction="row" justifyContent="center" sx={{mt: 4}}>
                            <Pagination
                                count={pagination.totalPages}
                                page={pagination.currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                                sx={{"& .MuiPagination-ul": {flexWrap: "nowrap"}}}
                            />
                        </Stack>
                    )}
                </Grid>
            </Grid>
        </Container>
    );
};

export default AdsListing;
